import { Box, Button, Chip, Stack, Tooltip, Typography, useMediaQuery, useTheme } from '@mui/material';
import { DataGridPremium, type GridColDef, type GridRenderCellParams } from '@mui/x-data-grid-premium';
import type { MonthRow } from '../../services/profit-share.service';
import { STATUS, closableFrom, money as fmt, monthLabel, signedSx, thisMonth } from './profit-share.format';

export interface MonthActions {
  close: (m: MonthRow) => void;
  reopen: (m: MonthRow) => void;
  markPaid: (m: MonthRow) => void;
  unmarkPaid: (m: MonthRow) => void;
}

const money = (field: keyof MonthRow, headerName: string, opts: Partial<GridColDef> = {}): GridColDef => ({
  field, headerName, type: 'number', flex: 1, minWidth: 95,
  valueFormatter: (v: number) => fmt(v ?? 0),
  ...opts,
});

export default function MonthsTable({ months, actions, showCompany = true }: { months: MonthRow[]; actions?: MonthActions; showCompany?: boolean }) {
  // On a phone, pins on both sides would leave the money no room.
  const narrow = useMediaQuery(useTheme().breakpoints.down('md'));
  // Months close in order: only the oldest open one, and only once it is over.
  const nextToClose = [...months].reverse().find((m) => m.status === 'estimate')?.month;
  const latestClosed = months.find((m) => m.status !== 'estimate')?.month;

  const columns: GridColDef[] = [
    { field: 'month', headerName: 'Month', width: 95, valueFormatter: (v: string) => monthLabel(v) },
    {
      field: 'status', headerName: 'Status', width: 100,
      renderCell: ({ row }: GridRenderCellParams<MonthRow>) => {
        const s = STATUS[row.status];
        const tip = row.status === 'paid' && row.statement
          ? `Paid ${fmt(row.statement.paid_amount ?? 0)} on ${row.statement.paid_on}${row.statement.paid_note ? ` · ${row.statement.paid_note}` : ''}`
          : row.status === 'estimate' ? 'From live tracked numbers; changes until the month is closed.' : 'Frozen when closed.';
        return <Tooltip title={tip}><Chip size="small" label={s.label} color={s.color} variant={row.status === 'estimate' ? 'outlined' : 'filled'} /></Tooltip>;
      },
    },
    money('revenue', 'Revenue'),
    money('cost', 'Ad cost'),
    money('adjustments', 'Adjustments'),
    money('expenses', 'Expenses'),
    money('net', 'Net', { renderCell: ({ value }) => <Box sx={signedSx(value)}>{fmt(value)}</Box> }),
    money('carry_in', 'Carried in', { description: 'Loss carried from earlier months, to be earned back before any payout.' }),
    money('waiver', 'Waived'),
    { field: 'share_pct', headerName: 'Share', width: 70, type: 'number', valueFormatter: (v: number | null) => (v == null ? '—' : `${v}%`) },
    money('payout', showCompany ? 'Payout' : 'Your payout', { renderCell: ({ value }) => <Box sx={{ fontWeight: 600 }}>{fmt(value)}</Box> }),
    money('carry_out', 'Carried out', { description: 'Loss carried into the next month.' }),
    ...(showCompany ? [money('company_profit', 'Company profit', { minWidth: 120, renderCell: ({ value }) => <Box sx={signedSx(value)}>{fmt(value)}</Box> })] : []),
  ];
  if (actions) {
    columns.push({
      field: 'actions', headerName: '', width: 200, sortable: false, filterable: false,
      renderCell: ({ row }: GridRenderCellParams<MonthRow>) => (
        <Stack direction="row" spacing={0.5}>
          {row.status === 'estimate' && row.month === nextToClose && row.month < thisMonth() && (Date.now() >= closableFrom(row.month).getTime()
            ? <Button size="small" variant="contained" onClick={() => actions.close(row)}>Close month</Button>
            : (
              <Tooltip title="Late ad cost is still syncing">
                <span><Button size="small" disabled>Closes {closableFrom(row.month).toISOString().slice(5, 10).replace('-', '/')}</Button></span>
              </Tooltip>
            ))}
          {row.status === 'closed' && row.payout > 0 && <Button size="small" onClick={() => actions.markPaid(row)}>Mark paid</Button>}
          {row.status === 'closed' && row.month === latestClosed && <Button size="small" color="warning" onClick={() => actions.reopen(row)}>Reopen</Button>}
          {row.status === 'paid' && <Button size="small" color="warning" onClick={() => actions.unmarkPaid(row)}>Unmark paid</Button>}
        </Stack>
      ),
    });
  }

  if (!months.length) return <Typography color="text.secondary">No months yet: set a share to start.</Typography>;
  return (
    <DataGridPremium
      rows={months}
      columns={columns}
      getRowId={(r: MonthRow) => r.month}
      density="compact"
      disableRowSelectionOnClick
      hideFooter
      autoHeight
      initialState={{ pinnedColumns: narrow ? { left: ['month'] } : { left: ['month', 'status'], right: actions ? ['actions'] : [] } }}
    />
  );
}
